import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { AlertCircle, Send } from "lucide-react";

const ReportIssue = () => {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    subject: '',
    description: '',
    priority: 'medium',
    hostInfo: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      // Get host information from localStorage
      const hostId = localStorage.getItem('hostId');
      const hostAuth = localStorage.getItem('hostAuth');
      
      const emailData = {
        to: 'segun@nairoke.com',
        subject: `KaraokeCore Issue Report: ${formData.subject}`,
        body: `
Issue Report from KaraokeCore Host/KJ

Subject: ${formData.subject}
Priority: ${formData.priority}

Description:
${formData.description}

Host Information:
- Host ID: ${hostId || 'Not available'}
- Authenticated: ${hostAuth || 'No'}
- Timestamp: ${new Date().toISOString()}
- User Agent: ${navigator.userAgent}
- URL: ${window.location.href}

Additional Info:
${formData.hostInfo}

---
This is an automated report from KaraokeCore application.
        `.trim()
      };

      // Create mailto link (since we don't have email service configured)
      const mailtoLink = `mailto:segun@nairoke.com?subject=${encodeURIComponent(emailData.subject)}&body=${encodeURIComponent(emailData.body)}`;
      
      // Open default email client
      window.location.href = mailtoLink;

      toast({
        title: "Issue Report Prepared",
        description: "Your default email client should open with the report. Please send the email to complete the report.",
      });

      // Reset form
      setFormData({
        subject: '',
        description: '',
        priority: 'medium',
        hostInfo: ''
      });
      setOpen(false);

    } catch (error) {
      console.error('Error preparing issue report:', error);
      toast({
        title: "Error",
        description: "Failed to prepare issue report. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="fixed bottom-4 right-4 h-11 sm:h-9 px-3 font-geist text-[12px] bg-console-red/10 border border-console-red/30 text-console-red hover:bg-console-red/20 hover:text-console-red rounded-[6px] z-50"
        >
          <AlertCircle className="h-4 w-4 mr-2" />
          Report Issue
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-console-red" />
            Report an Issue
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="subject">Subject</Label>
            <Input
              id="subject"
              value={formData.subject}
              onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
              placeholder="Brief description of the issue"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="priority">Priority</Label>
            <select
              id="priority"
              value={formData.priority}
              onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
              className="w-full px-3 py-2 border border-border rounded-md bg-background"
            >
              <option value="low">Low - Minor issue</option>
              <option value="medium">Medium - Affects functionality</option>
              <option value="high">High - Blocks important features</option>
              <option value="critical">Critical - System unusable</option>
            </select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Please describe the issue in detail. Include steps to reproduce if applicable."
              rows={4}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="hostInfo">Additional Information (Optional)</Label>
            <Textarea
              id="hostInfo"
              value={formData.hostInfo}
              onChange={(e) => setFormData(prev => ({ ...prev, hostInfo: e.target.value }))}
              placeholder="Any additional context, error messages, or information that might help resolve the issue."
              rows={3}
            />
          </div>

          <div className="bg-console-primary/10 border border-console-primary/30 rounded-[6px] p-3">
            <p className="font-geist text-[13px] text-console-primary">
              📧 This will open your default email client with a pre-filled report. 
              Your host information will be included automatically to help with troubleshooting.
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting || !formData.subject || !formData.description}
              className="flex-1"
            >
              {submitting ? (
                "Preparing..."
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Report
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ReportIssue;
